// src/components/TimeSelect.jsx
import React, { useId } from "react";
import { MINUTE_OPTIONS_15 } from "../constants/timeOptions";

const HOURS_12 = [
  "01",
  "02",
  "03",
  "04",
  "05",
  "06",
  "07",
  "08",
  "09",
  "10",
  "11",
  "12",
];

const MERIDIEMS = ["AM", "PM"];

function toParts(value, allowEmpty) {
  if (!value || typeof value !== "string") {
    return allowEmpty
      ? { hour12: "", minute: "", meridiem: "AM" }
      : { hour12: "09", minute: "00", meridiem: "AM" };
  }

  const [hStr, mStr] = value.split(":");
  let hours24 = parseInt(hStr, 10);
  let minutes = parseInt(mStr, 10);

  if (Number.isNaN(hours24)) hours24 = 9;
  if (Number.isNaN(minutes)) minutes = 0;

  // Snap minutes to nearest 15 just in case
  minutes = Math.round(minutes / 15) * 15;
  if (minutes === 60) {
    minutes = 0;
    hours24 = (hours24 + 1) % 24;
  }

  const meridiem = hours24 >= 12 ? "PM" : "AM";
  let hour12 = hours24 % 12;
  if (hour12 === 0) hour12 = 12;

  return {
    hour12: hour12.toString().padStart(2, "0"),
    minute: minutes.toString().padStart(2, "0"),
    meridiem,
  };
}

function fromParts(hour12Str, minuteStr, meridiem) {
  let hour12 = parseInt(hour12Str, 10);
  let minutes = parseInt(minuteStr, 10);

  if (Number.isNaN(hour12)) hour12 = 9;
  if (Number.isNaN(minutes)) minutes = 0;

  let hours24 = hour12 % 12;
  if (meridiem === "PM") {
    hours24 += 12;
  }

  return `${hours24.toString().padStart(2, "0")}:${minutes
    .toString()
    .padStart(2, "0")}`;
}

export default function TimeSelect({
  value,
  onChange,
  className = "",
  disabled = false,
  idPrefix,
  namePrefix,
  size = "sm",
  selectClassName = "",
  allowEmpty = false,
  emptyOptionLabel = "--",
  compact = false,
}) {
  const { hour12, minute, meridiem } = toParts(value, allowEmpty);
  const generatedId = useId();
  const sanitizedId = generatedId.replace(/[:]/g, "");
  const idBase = idPrefix || `time-select-${sanitizedId}`;
  const nameBase = namePrefix || `time_select_${sanitizedId}`;
  const allowBlank = Boolean(allowEmpty);
  const hasHour = Boolean(hour12);
  const isCompact = Boolean(compact);

  const handleHourChange = (e) => {
    if (allowBlank && e.target.value === "") {
      onChange?.("");
      return;
    }
    const next = fromParts(e.target.value, minute, meridiem);
    onChange?.(next);
  };

  const handleMinuteChange = (e) => {
    if (allowBlank && e.target.value === "") {
      onChange?.("");
      return;
    }
    const next = fromParts(hour12, e.target.value, meridiem);
    onChange?.(next);
  };

  const handleMeridiemChange = (e) => {
    if (allowBlank && !hasHour) {
      return;
    }
    const next = fromParts(hour12, minute, e.target.value);
    onChange?.(next);
  };

  const sizeClass = isCompact
    ? "h-9 rounded-lg border border-slate-300 bg-white px-2 text-sm"
    : size === "lg"
      ? "h-11 md:h-12 rounded-xl border border-slate-300 bg-white px-3 text-base md:text-lg"
      : "rounded border border-slate-300 bg-white px-2 py-1.5 text-sm";
  const mergedSelectClass = `${sizeClass} ${selectClassName}`.trim();
  const hourWidthClass = isCompact ? "w-14" : "w-16";
  const minuteWidthClass = isCompact ? "w-14" : "w-16";
  const meridiemWidthClass = isCompact ? "w-16" : "w-20";
  const gapClass = isCompact ? "gap-1.5" : "gap-2";

  return (
    <div className={`flex items-center ${gapClass} ${className}`.trim()}>
      <select
        id={`${idBase}-hour`}
        name={`${nameBase}_hour`}
        value={hour12}
        onChange={handleHourChange}
        disabled={disabled}
        className={`${hourWidthClass} ${mergedSelectClass}`.trim()}
      >
        {allowBlank && <option value="">{emptyOptionLabel}</option>}
        {HOURS_12.map((h) => (
          <option key={h} value={h}>
            {h}
          </option>
        ))}
      </select>
      <select
        id={`${idBase}-minute`}
        name={`${nameBase}_minute`}
        value={minute}
        onChange={handleMinuteChange}
        disabled={disabled || (allowBlank && !hasHour)}
        className={`${minuteWidthClass} ${mergedSelectClass}`.trim()}
      >
        {allowBlank && <option value="">{emptyOptionLabel}</option>}
        {MINUTE_OPTIONS_15.map((m) => (
          <option key={m} value={m}>
            {m}
          </option>
        ))}
      </select>
      <select
        id={`${idBase}-ampm`}
        name={`${nameBase}_ampm`}
        value={meridiem}
        onChange={handleMeridiemChange}
        disabled={disabled || (allowBlank && !hasHour)}
        className={`${meridiemWidthClass} ${mergedSelectClass}`.trim()}
      >
        {MERIDIEMS.map((m) => (
          <option key={m} value={m}>
            {m}
          </option>
        ))}
      </select>
    </div>
  );
}
